import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  listProcessBindings, saveProcessBinding, removeProcessBinding,
  listApprovalLevels, saveApprovalLevels,
  getApproverDirectory, APPROVER_TYPES, APPROVAL_ACTIONS, REJECTION_ACTIONS,
  type ProcessBindingRow, type ApprovalLevel, type ApproverDirectory,
} from '../../api/wfBindingsApi.js';
import { listDefinitions, type WorkflowDefinition } from '../../api/workflowApi.js';
import { sx, Field, SelectField, CheckboxField, toOptions } from './wfKit.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

/**
 * ApprovalSetupPage — where a Tenant Admin answers three questions in one place:
 *
 *   WHAT needs approval?  a business process            (t610 binding)
 *        ↓ mediated by
 *   WHICH workflow?       a definition from the designer (t045)
 *        ↓ whose levels are
 *   WHO approves?         a login id and/or a role       (t046 per level)
 *
 * Every option in every dropdown is fetched at run time from the tenant's own
 * masters. No process, workflow, role or user is written into this file, so a
 * new one becomes usable without a code change or a redeploy.
 *
 * This is a Tenant Admin screen: workflow routing is day-to-day operation, not
 * platform administration.
 */
export function ApprovalSetupPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ProcessBindingRow[] | null>(null);
  const [definitions, setDefinitions] = useState<readonly WorkflowDefinition[]>([]);
  const [directory, setDirectory] = useState<ApproverDirectory | null>(null);
  const [selected, setSelected] = useState<ProcessBindingRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadBindings = useCallback(() => {
    listProcessBindings(token)
      .then((r) => { setRows(r.processes); setError(null); })
      .catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    loadBindings();
    listDefinitions(token, { status: 'ACTIVE' })
      .then((r) => setDefinitions(r.rows))
      .catch((e) => setError((e as Error).message));
    getApproverDirectory(token)
      .then(setDirectory)
      .catch((e) => setError((e as Error).message));
  }, [token, loadBindings]);

  return (
    <main aria-label="approval-setup" style={sx.main}>
      <div style={sx.headerRow}>
        <h1>{t('wf.approvalSetup.title')}</h1>
      </div>
      <p style={{ color: 'var(--color-text-muted)', maxWidth: 760 }}>
        {t('wf.approvalSetup.intro')}
      </p>

      {error && <p role="alert" style={sx.error}>{error}</p>}
      {notice && <p style={sx.ok}>{notice}</p>}

      <ProcessSection
        rows={rows}
        definitions={definitions}
        selectedCode={selected?.processCode ?? null}
        onSelect={setSelected}
        onSaved={(m) => { setNotice(m); loadBindings(); }}
        onError={setError}
      />

      {selected && (
        <LevelsSection
          key={selected.processCode}
          process={selected}
          directory={directory}
          onNotice={setNotice}
          onError={setError}
        />
      )}
    </main>
  );
}

// ── 1. WHAT needs approval, and which workflow mediates it ───────────────────
function ProcessSection({ rows, definitions, selectedCode, onSelect, onSaved, onError }: {
  readonly rows: ProcessBindingRow[] | null;
  readonly definitions: readonly WorkflowDefinition[];
  readonly selectedCode: string | null;
  readonly onSelect: (r: ProcessBindingRow) => void;
  readonly onSaved: (message: string) => void;
  readonly onError: (m: string) => void;
}): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function save(row: ProcessBindingRow): Promise<void> {
    const workflowCode = draft[row.processCode] ?? row.workflowCode ?? '';
    if (!workflowCode) { onError(t('wf.approvalSetup.workflowRequired')); return; }
    setBusy(true);
    try {
      await saveProcessBinding(token, row.processCode, { workflowCode, isEnabled: true });
      onSaved(t('wf.approvalSetup.bindingSaved'));
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  }

  async function unbind(row: ProcessBindingRow): Promise<void> {
    setBusy(true);
    try {
      await removeProcessBinding(token, row.processCode);
      onSaved(t('wf.approvalSetup.bindingRemoved'));
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <section aria-label="process-binding-section" style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 16 }}>{t('wf.approvalSetup.processSection')}</h2>
      {!rows ? <p>{t('common.loading')}</p> : rows.length === 0 ? (
        <p data-testid="processes-empty" style={{ color: 'var(--color-text-muted)' }}>
          {t('wf.approvalSetup.noProcesses')}
        </p>
      ) : (
        <DataTable<ProcessBindingRow>
          ariaLabel="process-binding-table"
          testId="process-binding-table"
          rows={rows}
          rowKey={(r) => r.processCode}
          rowTestId={(r) => `process-row-${r.processCode}`}
          columns={[
            {
              key: 'process',
              header: t('wf.approvalSetup.colProcess'),
              // The name alone does not say what the approval actually gates.
              render: (r) => (
                <div>
                  <div>{t(r.labelKey)}</div>
                  {r.descriptionKey && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', maxWidth: 320 }}>
                      {t(r.descriptionKey)}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'module',
              header: t('wf.approvalSetup.colModule'),
              // The bare code is kept: support tickets quote "M13", but nobody
              // configuring an approval knows what M13 does.
              render: (r) => (
                <span data-testid={`module-${r.processCode}`}>
                  {r.moduleLabelKey ? `${r.moduleCode} \u2014 ${t(r.moduleLabelKey)}` : r.moduleCode}
                </span>
              ),
            },
            { key: 'entity', header: t('wf.approvalSetup.colEntity'), render: (r) => r.entityType },
            {
              key: 'workflow',
              header: t('wf.approvalSetup.colWorkflow'),
              render: (r) => (
                <SelectField
                  label=""
                  name={`workflow-${r.processCode}`}
                  value={draft[r.processCode] ?? r.workflowCode ?? ''}
                  onChange={(v) => setDraft((d) => ({ ...d, [r.processCode]: v }))}
                  placeholder={t('wf.approvalSetup.selectWorkflow')}
                  // Only workflows built for this entity type can serve it.
                  options={definitions
                    .filter((d) => !r.entityType || d.entityType === r.entityType)
                    .map((d) => ({ value: d.workflowCode, label: `${d.workflowName} (${d.workflowCode})` }))}
                />
              ),
            },
            {
              key: 'status',
              header: t('wf.approvalSetup.colStatus'),
              // "Bound" used to mean nothing more than "a workflow_code is
              // stored here". RKH's budget binding pointed at
              // 'BudgetApproval26-27-01', a code with no definition in that
              // tenant at all, and the screen still reported it Bound — so an
              // administrator had no way to see that budget approvals were
              // reaching nobody. The resolver already refuses such a binding
              // and logs it; the screen must say the same thing, or the two
              // disagree about whether the process is governed.
              render: (r) => {
                if (!r.workflowCode) return t('wf.approvalSetup.notBound');
                const resolves = definitions.some(
                  (d) => d.workflowCode === r.workflowCode,
                );
                if (!resolves) return t('wf.approvalSetup.brokenBinding');
                return r.isEnabled
                  ? t('wf.approvalSetup.bound')
                  : t('wf.approvalSetup.suspended');
              },
            },
            {
              key: 'actions',
              header: t('wf.common.actions'),
              render: (r) => (
                <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Button type="button" variant="primary" disabled={busy}
                    aria-label={`save-binding-${r.processCode}`} onClick={() => save(r)}>
                    {t('wf.approvalSetup.save')}
                  </Button>
                  <Button type="button" variant="secondary"
                    aria-label={`configure-levels-${r.processCode}`} onClick={() => onSelect(r)}>
                    {selectedCode === r.processCode
                      ? t('wf.approvalSetup.configuring')
                      : t('wf.approvalSetup.configureApprovers')}
                  </Button>
                  <Button type="button" variant="danger" disabled={busy || !r.bindingId}
                    aria-label={`unbind-${r.processCode}`} onClick={() => unbind(r)}>
                    {t('wf.approvalSetup.unbind')}
                  </Button>
                </span>
              ),
            },
          ]}
        />
      )}
    </section>
  );
}

// ── 2. WHO approves, level by level ──────────────────────────────────────────
function LevelsSection({ process, directory, onNotice, onError }: {
  readonly process: ProcessBindingRow;
  readonly directory: ApproverDirectory | null;
  readonly onNotice: (m: string) => void;
  readonly onError: (m: string) => void;
}): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [levels, setLevels] = useState<readonly ApprovalLevel[] | null>(null);
  const [workflowCode, setWorkflowCode] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, LevelDraft>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    listApprovalLevels(token, process.processCode)
      .then((r) => {
        setLevels(r.levels);
        setWorkflowCode(r.workflowCode);
        setDraft(Object.fromEntries(r.levels.map((l) => [l.stepId, {
          approverType: l.approverType ?? '',
          approverUserId: l.approverUserId ?? '',
          approverRoleCode: l.approverRoleCode ?? '',
          offset: l.hierarchyLevelOffset?.toString() ?? '',
          isMandatory: l.isMandatory,
          allowDelegate: l.allowDelegate,
          onApprovalAction: l.onApprovalAction ?? 'NEXT_LEVEL',
          approvalGotoStep: l.approvalGotoStep?.toString() ?? '',
          onRejectionAction: l.onRejectionAction ?? 'TERMINATE',
          rejectionGotoStep: l.rejectionGotoStep?.toString() ?? '',
          alternateWorkflowCode: l.alternateWorkflowCode ?? '',
        }])));
      })
      .catch((e) => onError((e as Error).message));
  }, [token, process.processCode, onError]);

  useEffect(() => { load(); }, [load]);

  const userOptions = (directory?.users ?? []).map((u) => ({
    value: u.userId,
    label: u.employeeCode ? `${u.displayName} (${u.employeeCode})` : u.displayName,
  }));
  const roleOptions = (directory?.roles ?? []).map((r) => ({
    value: r.roleCode, label: `${r.roleName} (${r.roleCode})`,
  }));
  const userLabel = (id: string | null): string =>
    userOptions.find((o) => o.value === id)?.label ?? '—';

  // Mirrors the server rule, so the user sees the gap before submitting rather
  // than as a 400. The server check is the one that actually protects the data.
  const incomplete = (levels ?? []).filter((l) => !isLevelComplete(draft[l.stepId]));

  async function saveAll(): Promise<void> {
    if (!levels) return;
    if (incomplete.length > 0) {
      onError(t('wf.approvalSetup.incompleteChain', {
        levels: incomplete.map((l) => `${l.approvalLevel} (${l.stepName})`).join(', '),
      }));
      return;
    }
    setBusy(true);
    try {
      await saveApprovalLevels(token, process.processCode, levels.map((l) => {
        const d = draft[l.stepId];
        const needsUser = d.approverType === 'SPECIFIC_USER';
        const needsRole = d.approverType === 'ROLE_BASED' || needsUser;
        return {
          stepId: l.stepId,
          approverType: d.approverType || null,
          approverUserId: needsUser ? d.approverUserId || null : null,
          approverRoleCode: needsRole ? d.approverRoleCode || null : null,
          hierarchyLevelOffset:
            d.approverType === 'HIERARCHY' && d.offset ? Number(d.offset) : null,
          allowDelegate: d.allowDelegate,
          onApprovalAction: d.onApprovalAction || 'NEXT_LEVEL',
          approvalGotoStep:
            d.onApprovalAction === 'GOTO_STEP' && d.approvalGotoStep
              ? Number(d.approvalGotoStep)
              : null,
          onRejectionAction: d.onRejectionAction || 'TERMINATE',
          rejectionGotoStep:
            d.onRejectionAction === 'RETURN_TO_STEP' && d.rejectionGotoStep
              ? Number(d.rejectionGotoStep)
              : null,
          alternateWorkflowCode:
            d.onRejectionAction === 'TRIGGER_ALTERNATE'
              ? d.alternateWorkflowCode || null
              : null,
        };
      }));
      onNotice(t('wf.approvalSetup.allLevelsSaved', { count: levels.length }));
      load();
    } catch (e) { onError((e as Error).message); } finally { setBusy(false); }
  }

  return (
    <section aria-label="approval-levels-section" style={{ ...sx.card }}>
      <h2 style={{ fontSize: 16, marginTop: 0 }}>
        {t('wf.approvalSetup.levelsSection', { process: t(process.labelKey) })}
      </h2>

      {/* Order matters. `workflowCode` starts null and is only filled when the
          fetch returns, so asking "is it unbound?" first made the screen say
          "bind this first" about a process that was already bound — it was
          reporting "I do not know yet" as "there is nothing here". `levels`
          is null until the same response lands, so it is what distinguishes
          the two. */}
      {!levels && !workflowCode ? (
        <p data-testid="levels-loading">{t('common.loading')}</p>
      ) : !workflowCode ? (
        <p data-testid="levels-unbound" style={sx.error}>{t('wf.approvalSetup.bindFirst')}</p>
      ) : (
        <>
          <p style={{ color: 'var(--color-text-muted)' }}>
            {t('wf.approvalSetup.mediatedBy', { workflow: workflowCode })}
            {levels && ` ${t('wf.approvalSetup.levelCount', { count: levels.length })}`}
          </p>

          {!levels ? <p>{t('common.loading')}</p> : levels.length === 0 ? (
            <p data-testid="levels-empty" style={sx.error}>{t('wf.approvalSetup.noLevels')}</p>
          ) : (
            <>
              <div style={{ display: 'grid', gap: 16 }}>
                {levels.map((lv) => (
                  <LevelEditor
                    key={lv.stepId}
                    level={lv}
                    total={levels.length}
                    value={draft[lv.stepId]}
                    onChange={(v) => setDraft((d) => ({ ...d, [lv.stepId]: v }))}
                    userOptions={userOptions}
                    roleOptions={roleOptions}
                    levelOptions={levels.map((o) => ({
                      value: String(o.approvalLevel),
                      label: `${o.approvalLevel} — ${o.stepName}`,
                    }))}
                    currentApprover={
                      lv.approverUserId ? userLabel(lv.approverUserId)
                        : lv.approverRoleCode ? lv.approverRoleCode : '—'
                    }
                  />
                ))}
              </div>

              {incomplete.length > 0 && (
                <p role="alert" data-testid="incomplete-chain" style={sx.error}>
                  {t('wf.approvalSetup.incompleteChain', {
                    levels: incomplete.map((l) => `${l.approvalLevel} (${l.stepName})`).join(', '),
                  })}
                </p>
              )}

              <div style={sx.actionBar}>
                <Button type="button" variant="primary"
                  disabled={busy || incomplete.length > 0}
                  aria-label="save-all-approvers" onClick={saveAll}>
                  {busy ? t('wf.approvalSetup.saving')
                    : t('wf.approvalSetup.saveAllApprovers', { count: levels.length })}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

type Opt = { readonly value: string; readonly label: string };

interface LevelDraft {
  approverType: string;
  approverUserId: string;
  approverRoleCode: string;
  offset: string;
  isMandatory: boolean;
  allowDelegate: boolean;
  onApprovalAction: string;
  approvalGotoStep: string;
  onRejectionAction: string;
  rejectionGotoStep: string;
  alternateWorkflowCode: string;
}

/** Which binding fields matter is decided by the approver type, never hardcoded per process. */
function approverFields(approverType: string): {
  needsUser: boolean; needsRole: boolean; needsOffset: boolean;
} {
  const needsUser = approverType === 'SPECIFIC_USER';
  return {
    needsUser,
    // Kept for SPECIFIC_USER too: the engine falls back to the role when the
    // named person is on leave or deactivated.
    needsRole: approverType === 'ROLE_BASED' || needsUser,
    needsOffset: approverType === 'HIERARCHY',
  };
}

/** Would this level actually resolve to somebody? Mirrors the server rule. */
function isLevelComplete(d: LevelDraft | undefined): boolean {
  if (!d || !d.approverType) return false;
  if (d.approverType === 'SPECIFIC_USER') return !!d.approverUserId;
  if (d.approverType === 'ROLE_BASED') return !!d.approverRoleCode;
  return true;
}

function LevelEditor({ level, total, value, onChange, userOptions, roleOptions, currentApprover, levelOptions }: {
  readonly level: ApprovalLevel;
  readonly total: number;
  readonly value: LevelDraft | undefined;
  readonly onChange: (v: LevelDraft) => void;
  readonly userOptions: readonly Opt[];
  readonly roleOptions: readonly Opt[];
  readonly currentApprover: string;
  /** Every level of this workflow, so a jump can only target a real one. */
  readonly levelOptions: readonly Opt[];
}): ReactNode {
  const { t } = useTranslation();
  const d = value ?? {
    approverType: '', approverUserId: '', approverRoleCode: '',
    offset: '', isMandatory: true, allowDelegate: false,
    onApprovalAction: 'NEXT_LEVEL', approvalGotoStep: '',
    onRejectionAction: 'TERMINATE', rejectionGotoStep: '',
    alternateWorkflowCode: '',
  };
  const { needsUser, needsRole, needsOffset } = approverFields(d.approverType);
  const set = (patch: Partial<LevelDraft>): void => onChange({ ...d, ...patch });
  const complete = isLevelComplete(d);

  return (
    <div aria-label={`level-editor-${level.approvalLevel}`}
      style={{
        border: `1px solid ${complete ? 'var(--color-border)' : 'var(--color-danger, #d1242f)'}`,
        borderRadius: 6, padding: 16,
      }}>
      <h3 style={{ fontSize: 14, marginTop: 0 }}>
        {t('wf.approvalSetup.levelHeadingOf', {
          level: level.approvalLevel, total, name: level.stepName,
        })}
      </h3>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
        {t('wf.approvalSetup.currentApprover', { approver: currentApprover })}
      </p>

      <SelectField label={t('wf.common.approverType')} name={`type-${level.stepId}`}
        value={d.approverType} onChange={(v) => set({ approverType: v })}
        options={toOptions([...APPROVER_TYPES])} placeholder={t('wf.common.noneOption')} required />

      {needsUser && (
        <SelectField label={t('wf.approvalSetup.approverUser')} name={`user-${level.stepId}`}
          value={d.approverUserId} onChange={(v) => set({ approverUserId: v })}
          options={userOptions} required />
      )}
      {needsRole && (
        <SelectField
          label={needsUser ? t('wf.approvalSetup.fallbackRole') : t('wf.approvalSetup.approverRole')}
          name={`role-${level.stepId}`} value={d.approverRoleCode}
          onChange={(v) => set({ approverRoleCode: v })}
          options={roleOptions} required={!needsUser} />
      )}
      {needsOffset && (
        <Field label={t('wf.approvalSetup.hierarchyOffset')} name={`offset-${level.stepId}`}
          type="number" value={d.offset} onChange={(v) => set({ offset: v })} />
      )}

      {/* "Mandatory" was removed here as it was from the steps designer. It
          belonged to an older step shape where it marked branches that only
          ran sometimes — the Rework and Reject paths — alongside can_reject and
          can_return. That routing is decided by the two dropdowns below and by
          step conditions, and nothing has read is_mandatory since. A box that
          changes nothing is worse than no box: it invites an administrator to
          believe a level is optional when the engine will wait for it. */}
      <CheckboxField label={t('wf.approvalSetup.allowDelegate')} name={`delegate-${level.stepId}`}
        checked={d.allowDelegate} onChange={(v) => set({ allowDelegate: v })} />

      {/* Routing. Both decisions are configured here; without this the engine
          fell through to the next level on approve and terminated on reject. */}
      <fieldset style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: 12, marginTop: 12 }}>
        <legend style={{ fontSize: 12, padding: '0 6px' }}>Routing</legend>

        <SelectField label="On approve, go to" name={`onapprove-${level.stepId}`}
          value={d.onApprovalAction} onChange={(v) => set({ onApprovalAction: v })}
          options={toOptions([...APPROVAL_ACTIONS])} required />
        {d.onApprovalAction === 'GOTO_STEP' && (
          <SelectField label="Approve target level" name={`approvegoto-${level.stepId}`}
            value={d.approvalGotoStep} onChange={(v) => set({ approvalGotoStep: v })}
            options={levelOptions.filter((o) => o.value !== String(level.approvalLevel))}
            required />
        )}

        <SelectField label="On reject, go to" name={`onreject-${level.stepId}`}
          value={d.onRejectionAction} onChange={(v) => set({ onRejectionAction: v })}
          options={toOptions([...REJECTION_ACTIONS])} required />
        {d.onRejectionAction === 'RETURN_TO_STEP' && (
          <SelectField label="Reject target level" name={`rejectgoto-${level.stepId}`}
            value={d.rejectionGotoStep} onChange={(v) => set({ rejectionGotoStep: v })}
            options={levelOptions.filter((o) => o.value !== String(level.approvalLevel))}
            required />
        )}
        {d.onRejectionAction === 'TRIGGER_ALTERNATE' && (
          <Field label="Alternate workflow code" name={`altwf-${level.stepId}`}
            value={d.alternateWorkflowCode}
            onChange={(v) => set({ alternateWorkflowCode: v })} />
        )}
      </fieldset>

      {!complete && (
        <p style={{ ...sx.error, marginBottom: 0 }}>{t('wf.approvalSetup.levelNeedsApprover')}</p>
      )}
    </div>
  );
}
